import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, LayersIcon, SquareIcon, FootprintsIcon, MountainIcon, GridIcon, TreesIcon, ChevronDownIcon } from 'lucide-react';
import { FCIQuoteForm } from './FCIQuoteForm';
interface FCILandingPageProps {
  initialService?: string;
  onClose: () => void;
}
export function FCILandingPage({
  initialService,
  onClose
}: FCILandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState<string>(initialService || '');
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialService) {
      setSelectedService(initialService);
    }
  }, [initialService]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedService) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <FCIQuoteForm initialZip={zipCode} serviceSlug={selectedService} onClose={onClose} data-id="element-1297" />;
  }
  const services = [{
    id: 'carpet',
    name: 'Carpet',
    icon: <LayersIcon className="w-6 h-6" data-id="element-1298" />
  }, {
    id: 'laminate',
    name: 'Laminate',
    icon: <SquareIcon className="w-6 h-6" data-id="element-1299" />
  }, {
    id: 'vinyl',
    name: 'Vinyl',
    icon: <FootprintsIcon className="w-6 h-6" data-id="element-1300" />
  }, {
    id: 'stone',
    name: 'Stone',
    icon: <MountainIcon className="w-6 h-6" data-id="element-1301" />
  }, {
    id: 'tile',
    name: 'Tile',
    icon: <GridIcon className="w-6 h-6" data-id="element-1302" />
  }, {
    id: 'wood-floors',
    name: 'Wood Floors',
    icon: <TreesIcon className="w-6 h-6" data-id="element-1303" />
  }];
  const serviceLabel = services.find(s => s.id === selectedService)?.name || '';
  return <div className="min-h-screen bg-[#FAFAFA] font-sans" data-id="element-1304">
      {/* Header */}
      <header className="bg-white py-4 px-6 md:px-12 flex justify-center items-center shadow-sm relative z-20" data-id="element-1305">
        <div className="flex flex-col items-center" data-id="element-1306">
          <div className="flex items-baseline gap-2" data-id="element-1307">
            <img src="https://cdn.magicpatterns.com/uploads/5wpLpVezYeAkEh7NG2YmbK/FCI_Logo.webp" alt="Floor Coverings International" className="h-14 md:h-16 object-contain" data-id="element-1308" />
          </div>
          <span className="text-[10px] md:text-sm font-semibold text-gray-500" data-id="element-1309">
            Carpet &bull; Hardwood &bull; Laminate &bull; Tile &bull; Vinyl
            &bull; Stone
          </span>
        </div>
      </header>

      {/* Offer Banner */}
      <section className="bg-[#8B7D6B] py-4 md:py-6 border-t border-[#5C5145]" data-id="element-1310">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1311">
          <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight" data-id="element-1312">
            The Flooring Store at Your Door&reg;
          </h2>
          <p className="text-stone-200 text-sm mt-1" data-id="element-1313">
            Shop thousands of flooring options from the comfort of your home
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-12 pb-24 md:pt-20 md:pb-32 overflow-hidden" data-id="element-1314">
        <div className="absolute inset-0 z-0" data-id="element-1315">
          <img src="https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=1920&q=80" alt="Beautiful hardwood flooring" className="w-full h-full object-cover" data-id="element-1316" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" data-id="element-1317" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1318">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-1319">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1320">
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-1321">
                The Flooring Store at Your Door
              </h1>
              <p className="text-lg md:text-xl font-medium text-gray-200 drop-shadow-md max-w-lg" data-id="element-1322">
                Our Design Associates bring a mobile flooring showroom right to
                your door with thousands of samples — so you can see, touch, and
                compare options in your own home's lighting.
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-1323">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-1324">
                <h2 className="text-xl font-bold text-[#8B7D6B] text-center mb-6" data-id="element-1325">
                  {initialService ? 'Enter Your Zip Code To Start' : 'Select Flooring & Enter Your Zip'}
                </h2>

                {/* Service dropdown */}
                {!initialService && <div className="mb-6 relative" ref={dropdownRef} data-id="element-1326">
                    <button onClick={() => setDropdownOpen(!dropdownOpen)} className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200 bg-stone-50 ${selectedService ? 'border-[#8B7D6B]' : 'border-stone-300'} ${dropdownOpen ? 'border-[#8B7D6B] shadow-md' : 'hover:border-stone-400'}`} data-id="element-1327">
                      <div className="flex items-center gap-3" data-id="element-1328">
                        {selectedService ? <>
                            <span className="text-[#8B7D6B]" data-id="element-1329">
                              {services.find(s => s.id === selectedService)?.icon}
                            </span>
                            <span className="font-bold text-[#8B7D6B] text-lg" data-id="element-1330">
                              {services.find(s => s.id === selectedService)?.name}
                            </span>
                          </> : <span className="text-gray-400 font-semibold text-lg" data-id="element-1331">
                            Choose flooring type...
                          </span>}
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 text-[#8B7D6B] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-1332" />
                    </button>

                    <AnimatePresence data-id="element-1333">
                      {dropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scaleY: 1
                  }} exit={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border-2 border-stone-300 shadow-xl z-30 overflow-hidden origin-top" data-id="element-1334">
                          {services.map(svc => <button key={svc.id} onClick={() => {
                      setSelectedService(svc.id);
                      setDropdownOpen(false);
                    }} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${selectedService === svc.id ? 'bg-stone-50 text-[#8B7D6B]' : 'bg-white text-gray-700 hover:bg-stone-50 hover:text-[#8B7D6B]'} border-b border-stone-200 last:border-b-0`} data-id="element-1335">
                              <span className={`${selectedService === svc.id ? 'text-[#8B7D6B]' : 'text-gray-400'}`} data-id="element-1336">
                                {svc.icon}
                              </span>
                              <span className="font-bold text-lg" data-id="element-1337">
                                {svc.name}
                              </span>
                              {selectedService === svc.id && <CheckCircle2Icon className="w-5 h-5 text-[#8B7D6B] ml-auto" data-id="element-1338" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}

                {/* Show selected service badge when pre-selected */}
                {initialService && <div className="flex items-center justify-center gap-2 mb-6 bg-stone-50 border border-stone-300 rounded-lg px-4 py-2.5" data-id="element-1339">
                    {services.find(s => s.id === initialService)?.icon}
                    <span className="font-bold text-[#8B7D6B]" data-id="element-1340">
                      {serviceLabel}
                    </span>
                  </div>}

                {/* Zip Code Input */}
                <div className="relative mb-6" data-id="element-1341">
                  <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Enter Zip Code" className="w-full h-16 px-6 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#8B7D6B] focus:ring-1 focus:ring-[#8B7D6B] transition-colors" maxLength={5} data-id="element-1342" />
                  {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-600" data-id="element-1343">
                      <CheckCircle2Icon className="w-8 h-8" data-id="element-1344" />
                    </div>}
                </div>

                <button onClick={handleContinue} disabled={!isValidZip || !selectedService} className={`w-full h-16 rounded-xl font-bold text-xl text-white transition-all shadow-lg ${isValidZip && selectedService ? 'bg-[#FF6F00] hover:bg-orange-700 shadow-orange-500/30 hover:shadow-orange-500/40 cursor-pointer' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-1345">
                  Get Free Estimate
                </button>

                {/* Trust Badges */}
                <div className="mt-8 flex items-center justify-center gap-4" data-id="element-1346">
                  <div className="flex items-center gap-2 bg-stone-50 px-3 py-1.5 rounded-lg border border-stone-300" data-id="element-1347">
                    <ShieldCheckIcon className="w-5 h-5 text-stone-600" data-id="element-1348" />
                    <span className="text-xs font-bold text-stone-800 uppercase tracking-tight" data-id="element-1349">
                      Quality Guaranteed
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200" data-id="element-1350">
                    <div className="font-black text-slate-700 text-sm" data-id="element-1351">BBB</div>
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-tight" data-id="element-1352">
                      A+ Rating
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Accent Bar */}
      <div className="w-full h-3 bg-[#8B7D6B]" data-id="element-1353" />

      {/* Why FCI Section */}
      <section className="py-20 bg-white border-b border-gray-100" data-id="element-1354">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1355">
          <div className="text-center mb-16" data-id="element-1356">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-1357">
              Why Floor Coverings International?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1358">
            {[{
            title: 'Mobile Showroom Experience',
            desc: 'We bring thousands of flooring samples directly to your home so you can see how they look in your own lighting and décor.'
          }, {
            title: 'Expert Design Associates',
            desc: 'Our trained Design Associates guide you through every option, helping you find the perfect floor for your style and budget.'
          }, {
            title: 'Professional Installation',
            desc: "Certified installers handle everything — from moving furniture to the final cleanup — so you don't have to lift a finger."
          }, {
            title: 'Satisfaction Guarantee',
            desc: 'We stand behind every installation with a comprehensive satisfaction guarantee and quality craftsmanship warranty.'
          }].map((feature, idx) => <div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-[#8B7D6B] hover:shadow-md transition-shadow" data-id="element-1359">
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1360">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1361">{feature.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 bg-gray-50" data-id="element-1362">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1363">
          <div className="text-center mb-16" data-id="element-1364">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-1365">
              What to Expect
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-1366">
            {[{
            title: 'Browse & Choose',
            desc: 'Explore our wide selection of carpet, hardwood, laminate, tile, vinyl, and stone flooring. Find inspiration for every room in your home.',
            img: 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=400&h=400&fit=crop'
          }, {
            title: 'In-Home Design Consultation',
            desc: "A Design Associate brings our mobile showroom to your door with thousands of samples. See and compare options in your own home's lighting.",
            img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
          }, {
            title: 'Stress-Free Installation',
            desc: 'Our certified professionals handle everything — furniture moving, old flooring removal, expert installation, and thorough cleanup.',
            img: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=400&fit=crop'
          }].map((item, idx) => <motion.div key={idx} className="flex flex-col items-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2,
            duration: 0.5
          }} data-id="element-1367">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 shadow-lg border-4 border-white" data-id="element-1368">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-1369" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1370">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1371">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#5C5145] py-16" data-id="element-1372">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1373">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-1374">
            <div className="flex flex-col gap-8" data-id="element-1375">
              <div className="flex flex-col" data-id="element-1376">
                <img src="https://cdn.magicpatterns.com/uploads/5wpLpVezYeAkEh7NG2YmbK/FCI_Logo.webp" alt="Floor Coverings International" className="h-10 brightness-0 invert object-contain object-left mb-2" data-id="element-1377" />
                <span className="text-[10px] font-semibold text-white/90" data-id="element-1378">
                  Carpet &bull; Hardwood &bull; Laminate &bull; Tile &bull;
                  Vinyl &bull; Stone
                </span>
              </div>
              <p className="text-xs text-white/80 font-medium" data-id="element-1379">
                &copy; {new Date().getFullYear()} findservicequotes.io. All
                Rights Reserved.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-1380">
              <div className="flex flex-col gap-4" data-id="element-1381">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1382">
                  Home
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1383">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1384">
                  Terms Of Use
                </a>
              </div>
              <div className="flex flex-col gap-4" data-id="element-1385">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1386">
                  Accessibility Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-1387">
                  Do Not Sell My Personal Information
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8" data-id="element-1388">
            <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-1389">
              Floor Coverings International services are available nationwide.
              All estimates are free and no-obligation. Actual project costs may
              vary based on scope, materials, and local conditions. Licensed and
              insured. Contact us for full warranty details.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}